import { SHA256 } from 'crypto-js';
import Base64 from 'crypto-js/enc-base64';

interface IAuthStorageFingerprint {
    id: string;
    access: string;
    refresh: string;
}

interface ILoginPayload {
    codeChallenge: string;
    submittedState: string;
}

const SCOPED_TOKEN_KEYS = ['access_token', 'refresh_token'] as const;
const GLOBAL_ITEM_KEYS = ['account_id', 'v2_auth', 'code_verifier'] as const;
const AUTH_ITEM_KEYS = [...SCOPED_TOKEN_KEYS, ...GLOBAL_ITEM_KEYS] as const;

/**
 * Writes during account switch must land on the *target* account's keys even while the
 * URL still shows the previous account (e.g. provider "switch account" then `href="/"`).
 * Module-level because AuthProvider constructs a new AuthStorage on each render while the
 * API middleware keeps the first instance.
 */
let writeAccountIdOverride: string | undefined;

function generateRandomString(length: number): string {
    const possible: string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    if (typeof crypto === 'undefined' || typeof crypto.getRandomValues !== 'function') {
        throw new Error('Web Crypto getRandomValues is required to generate PKCE values');
    }
    const bytes = new Uint8Array(length);
    const maxUnbiased = Math.floor(256 / possible.length) * possible.length;
    const chars: string[] = [];
    while (chars.length < length) {
        crypto.getRandomValues(bytes);
        for (let i = 0; i < bytes.length && chars.length < length; i++) {
            const value = bytes[i];
            if (value < maxUnbiased) {
                chars.push(possible.charAt(value % possible.length));
            }
        }
    }
    return chars.join('');
}

function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function generateCodeVerifier(): string {
    return generateRandomString(32);
}

function generateCodeChallenge(codeVerifier: string): string {
    return base64URL(SHA256(codeVerifier));
}

function base64URL(str: any): string {
    return str.toString(Base64).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
}

const ACCOUNT_ID_PATH = /^\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})(?:\/|$)/;

export function accountIdFromPath(pathname: string): string | undefined {
    const match = pathname.match(ACCOUNT_ID_PATH);
    return match?.[1];
}

function persist(key: string, value: string): void {
    window.sessionStorage.setItem(key, value);
    window.localStorage.setItem(key, value);
}

function forget(key: string): void {
    window.sessionStorage.removeItem(key);
    window.localStorage.removeItem(key);
}

function readLocal(key: string): string | null {
    return window.localStorage.getItem(key);
}

function storageKeys(storage: Storage): string[] {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key) {
            keys.push(key);
        }
    }
    return keys;
}

/**
 * Facade for persistent storage. Handles decision making for localStorage
 * vs sessionStorage persistence automatically.
 *
 * Token keys are scoped by the URL account prefix (`/:accountId/...`) so two tabs
 * logged into different accounts do not overwrite one another:
 *   support_<accountId>_access_token
 *   provider_<accountId>_access_token
 *
 * Pre-scoping session keys (`support_access_token`, `support_refresh_token`, …)
 * are a one-time bootstrap: copied into the current account's slot, then removed.
 * `code_verifier`, `v2_auth`, and `account_id` stay unscoped. `account_id` is the
 * last-active pointer used on prefix-less URLs such as `/` after an account switch.
 */
export class AuthStorage {
    public clientId: string;
    private prefix: string;

    constructor(clientId: string) {
        this.clientId = clientId;
        this.prefix = (clientId.split('_')[0] || clientId).toLowerCase();
    }

    /**
     * Direct subsequent writes at this account's keys, even if the URL still shows another.
     * Pass `undefined` to return to URL-derived scoping.
     */
    public scopeWritesTo(accountId: string | undefined): void {
        writeAccountIdOverride = accountId;
    }

    /* ------------- public utilities ------------- */

    public loginPayload(): ILoginPayload {
        this.codeVerifier = generateCodeVerifier();
        return {
            codeChallenge: generateCodeChallenge(this.codeVerifier),
            submittedState: generateRandomString(32),
        };
    }

    public logoutClear(): void {
        const prefixes = AUTH_ITEM_KEYS.map(suffix => `${this.prefix}_${suffix}`);
        const scopedPattern = new RegExp(
            `^${escapeRegExp(this.prefix)}_[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}_(?:${AUTH_ITEM_KEYS.join('|')})$`,
        );
        for (const storage of [window.localStorage, window.sessionStorage]) {
            for (const key of storageKeys(storage)) {
                if (prefixes.includes(key) || scopedPattern.test(key)) {
                    storage.removeItem(key);
                }
            }
        }
        // Demo mode deliberately SURVIVES logout (NS-14). It is a property of this browser, not of
        // the session, and this runs on token-expiry too — so clearing it here silently returned a
        // salesperson to real production data mid-demo, with a prospect watching and only the
        // sidebar chip changing to say so.
        writeAccountIdOverride = undefined;
    }

    /* ------------- getters ------------- */

    public get fingerprint(): IAuthStorageFingerprint {
        const abbr = (s: string | undefined): string => (s ?? '').slice(0, 6);
        return {
            id: abbr(this.accountId),
            access: abbr(this.accessToken),
            refresh: abbr(this.refreshToken),
        };
    }

    public get accountId(): string | undefined {
        return this.getStringOrUndefined(readLocal(this.unscopedKey('account_id')));
    }
    public set accountId(accountId: string | undefined) {
        if (accountId == null) {
            forget(this.unscopedKey('account_id'));
        } else {
            persist(this.unscopedKey('account_id'), accountId);
        }
    }

    public get v2Auth(): string | undefined {
        return this.getStringOrUndefined(readLocal(this.unscopedKey('v2_auth')));
    }

    public set v2Auth(v2Auth: string | undefined) {
        if (v2Auth === undefined || v2Auth === null) {
            forget(this.unscopedKey('v2_auth'));
        } else {
            persist(this.unscopedKey('v2_auth'), v2Auth);
        }
    }

    public get accessToken(): string | undefined {
        return this.getItem('access_token');
    }
    public set accessToken(accessToken: string | undefined) {
        this.setItem('access_token', accessToken);
    }

    public get refreshToken(): string | undefined {
        return this.getItem('refresh_token');
    }
    public set refreshToken(refreshToken: string | undefined) {
        this.setItem('refresh_token', refreshToken);
    }

    public get codeVerifier(): string | undefined {
        return this.getItem('code_verifier');
    }
    public set codeVerifier(codeVerifier: string | undefined) {
        this.setItem('code_verifier', codeVerifier);
    }

    private accountIdForWrite(): string | undefined {
        return writeAccountIdOverride ?? this.accountIdFromLocation();
    }

    private accountIdFromLocation(): string | undefined {
        if (typeof window === 'undefined' || !window.location) {
            return undefined;
        }
        return accountIdFromPath(window.location.pathname);
    }

    private unscopedKey(key: string): string {
        return `${this.prefix}_${key}`;
    }

    private scopedKey(key: string, accountId: string): string {
        return `${this.prefix}_${accountId}_${key}`;
    }

    private getStringOrUndefined(value: string | null): string | undefined {
        return value === null ? undefined : value;
    }

    private scopedCodeVerifierPattern(): RegExp {
        return new RegExp(
            `^${escapeRegExp(this.prefix)}_[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}_code_verifier$`,
        );
    }

    private removeScopedCodeVerifierLeftovers(): void {
        const pattern = this.scopedCodeVerifierPattern();
        for (const storage of [window.localStorage, window.sessionStorage]) {
            for (const key of storageKeys(storage)) {
                if (pattern.test(key)) {
                    storage.removeItem(key);
                }
            }
        }
    }

    private hasScopedAccessToken(): boolean {
        return this.scopedAccountIds().length > 0;
    }

    private lastActiveAccountId(): string | undefined {
        return this.getStringOrUndefined(readLocal(this.unscopedKey('account_id')));
    }

    private readScoped(key: string, accountId: string | undefined): string | undefined {
        if (!accountId) {
            return undefined;
        }
        return this.getStringOrUndefined(readLocal(this.scopedKey(key, accountId)));
    }

    private removeUnscopedAuthKeys(): void {
        for (const key of SCOPED_TOKEN_KEYS) {
            forget(this.unscopedKey(key));
        }
        if (this.hasScopedAccessToken()) {
            forget(this.unscopedKey('code_verifier'));
            this.removeScopedCodeVerifierLeftovers();
        }
    }

    private adoptUnscopedInto(accountId: string): void {
        for (const key of SCOPED_TOKEN_KEYS) {
            const unscoped = readLocal(this.unscopedKey(key));
            if (unscoped === null) {
                continue;
            }
            persist(this.scopedKey(key, accountId), unscoped);
        }
        this.accountId = accountId;
        this.removeUnscopedAuthKeys();
    }

    private scopedAccountIds(): string[] {
        const pattern = new RegExp(
            `^${escapeRegExp(this.prefix)}_([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})_access_token$`,
        );
        const ids: string[] = [];
        for (const key of storageKeys(window.localStorage)) {
            const match = key.match(pattern);
            if (match?.[1]) {
                ids.push(match[1]);
            }
        }
        return ids;
    }

    private readLeftoverScopedCodeVerifier(): string | undefined {
        const pattern = this.scopedCodeVerifierPattern();
        for (const key of storageKeys(window.localStorage)) {
            if (pattern.test(key)) {
                return this.getStringOrUndefined(readLocal(key));
            }
        }
        return undefined;
    }

    private getCodeVerifier(): string | undefined {
        const unscoped = this.getStringOrUndefined(readLocal(this.unscopedKey('code_verifier')));
        if (unscoped) {
            this.removeScopedCodeVerifierLeftovers();
            return unscoped;
        }
        const leftover = this.readLeftoverScopedCodeVerifier();
        if (leftover) {
            persist(this.unscopedKey('code_verifier'), leftover);
            this.removeScopedCodeVerifierLeftovers();
            return leftover;
        }
        return undefined;
    }

    private getItem(key: string): string | undefined {
        if (key === 'code_verifier') {
            return this.getCodeVerifier();
        }
        const locationAccountId = this.accountIdFromLocation();
        if (locationAccountId) {
            const scoped = this.readScoped(key, locationAccountId);
            if (scoped) {
                this.removeUnscopedAuthKeys();
                return scoped;
            }
            const unscoped = this.getStringOrUndefined(readLocal(this.unscopedKey(key)));
            if (unscoped) {
                this.adoptUnscopedInto(locationAccountId);
                return unscoped;
            }
            // Prefix present but this account has no slot yet (new tab after a switch).
            // Use last-active tokens only to authenticate switchAccount — do not copy them here.
            return this.readScoped(key, this.lastActiveAccountId());
        }
        return (
            this.readScoped(key, this.lastActiveAccountId()) ??
            this.getStringOrUndefined(readLocal(this.unscopedKey(key)))
        );
    }

    // NB: always write BOTH sessionStorage and localStorage
    // other tabs continue using session values even if local changes
    private setItem(key: string, value: string | undefined): void {
        if (value != null) {
            if (key === 'code_verifier') {
                persist(this.unscopedKey(key), value);
                this.removeScopedCodeVerifierLeftovers();
                return;
            }
            const accountId = this.accountIdForWrite();
            if (accountId) {
                persist(this.scopedKey(key, accountId), value);
                this.accountId = accountId;
                this.removeUnscopedAuthKeys();
            } else {
                persist(this.unscopedKey(key), value);
            }
        }
    }
    private removeItem(key: string, prefixKey: boolean = true): void {
        if (!prefixKey) {
            forget(key);
            return;
        }
        forget(this.unscopedKey(key));
        const accountId = this.accountIdForWrite();
        if (accountId) {
            forget(this.scopedKey(key, accountId));
        }
    }
}
